/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from 'react';
import { View, Platform, Dimensions } from 'react-native';
import { COLORS } from '../../../constants/themes';
import { useTheme } from '../../../theme';

// Cross-platform import for Victory components
let VictoryChart: any,
  VictoryBar: any,
  VictoryAxis: any,
  VictoryTheme: any,
  VictoryLabel: any;

if (Platform.OS === 'web') {
  // @ts-ignore
  ({
    VictoryChart,
    VictoryBar,
    VictoryAxis,
    VictoryTheme,
    VictoryLabel,
  } = require('victory'));
} else {
  // @ts-ignore
  ({
    VictoryChart,
    VictoryBar,
    VictoryAxis,
    VictoryTheme,
    VictoryLabel,
  } = require('victory-native'));
}

interface MetricsChartProps {
  data?: Array<{
    name: string;
    value: number; // percentage value (0-100)
  }>;
  height?: number;
  width?: number;
  isLoading?: boolean;
}

/**
 * MetricsChart Component
 * Cross-platform bar chart using Victory for both web and mobile
 * Displays metrics data with responsive sizing
 */
const MetricsChart: React.FC<MetricsChartProps> = ({
  data = [],
  height = 300,
  width,
  isLoading = false,
}) => {
  const { colors } = useTheme();
  const { width: screenWidth } = Dimensions.get('window');
  const isWeb = Platform.OS === 'web';
  const isNative = Platform.OS === 'ios' || Platform.OS === 'android';
  const isIpad = Platform.OS === 'ios' && screenWidth >= 768;
  const isIphone = Platform.OS === 'ios' && screenWidth < 768;
  
  // Determine font size based on platform
  const axisFontSize = isNative ? 11 : 18;
  
  // Determine chart padding based on platform - optimized for text wrapping
  const chartPadding = isWeb 
    ? { top: 40, bottom: 60, left: 160, right: 60 }
    : isIpad
    ? { top: 35, bottom: 60, left: 130, right: 100 }
    : { top: 20, bottom: 40, left: 110, right: 30 };
  
  // Determine chart height based on platform
  const chartHeight = isWeb ? 300 : isIpad ? 290 : 250;
  
  // Determine tick count for better spacing on mobile
  const tickCount = isWeb ? 11 : isIpad ? 8 : 6;
  
  // Calculate dynamic width based on platform and screen size
  const chartWidth = useMemo(() => {
    if (width) return width;
    // For web, use more space; for iPad/mobile, constraint appropriately
    if (isWeb) {
      return Math.min(screenWidth - 48, 800);
    } else if (isIpad) {
      return screenWidth - 40; // Even more generous for iPad landscape
    } else {
      return screenWidth - 32; // iPhone
    }
  }, [width, screenWidth, isWeb, isIpad]);

  // Transform data to Victory format with percentage values
  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }
    // For horizontal chart: x = location name (categorical), y = percentage value (numeric)
    return data.map((item, index) => ({
      x: item.name,
      y: item.value,
      value: item.value,
      index,
    }));
  }, [data]);

  // Color function: green for high (80+), yellow for medium (50-79), red for low (<50)
  const getBarColor = (value: number): string => {
    if (value >= 80) return COLORS.SUCCESS || '#129F14';
    if (value >= 50) return COLORS.WARNING || '#F59E0B';
    return COLORS.ERROR || '#AB2B09';
  };

  // Don't render if no data or loading
  if (isLoading || chartData.length === 0) {
    return null;
  }

  return (
    <View
      style={{
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 12,
        paddingHorizontal: isWeb ? 0 : 8,
      }}
    >
      <VictoryChart
        width={chartWidth}
        height={chartHeight}
        domainPadding={{ x: 20 }}
        horizontal
        theme={VictoryTheme.material}
        padding={chartPadding}
        standalone={true}
      >
        {/* Y-Axis (shows percentage values - becomes horizontal when horizontal={true}) */}
        <VictoryAxis
          dependentAxis
          tickFormat={(t: number) => `${Math.round(t)}%`}
          domain={[0, 100]}
          tickCount={tickCount}
          style={{
            axis: { stroke: colors.border.default, strokeWidth: 1 },
            grid: { stroke: colors.border.divider, strokeWidth: 0.5 },
            tickLabels: {
              fontSize: axisFontSize,
              fill: colors.text.secondary,
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
            },
            ticks: { stroke: colors.border.default, size: 5 },
          }}
        />

        {/* X-Axis (shows location names - becomes vertical when horizontal={true}) */}
        <VictoryAxis
          tickFormat={(text: any) => {
            // For native platforms, split long text into multiple lines
            if (isNative && typeof text === 'string' && text.length > 15) {
              // Split by spaces and create lines of appropriate length
              const words = text.split(' ');
              const lines: string[] = [];
              let currentLine = '';
              
              words.forEach((word) => {
                const testLine = currentLine ? `${currentLine} ${word}` : word;
                if (testLine.length > 15 && currentLine) {
                  lines.push(currentLine);
                  currentLine = word;
                } else {
                  currentLine = testLine;
                }
              });
              if (currentLine) lines.push(currentLine);
              
              return lines.join('\n');
            }
            return text;
          }}
          tickLabelComponent={
            <VictoryLabel 
              angle={0} 
              textAnchor="end" 
              verticalAnchor="middle"
              style={{
                fontSize: axisFontSize,
                fill: colors.text.primary,
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
              }}
              lineHeight={isNative ? 1.3 : 1}
              dx={isNative ? -5 : 0}
            />
          }
          style={{
            axis: { stroke: colors.border.default, strokeWidth: 1 },
            tickLabels: {
              fontSize: axisFontSize,
              fill: colors.text.primary,
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
            },
            ticks: { stroke: colors.border.default, size: 0 },
          }}
        />

        {/* Horizontal Bar Chart */}
        <VictoryBar
          data={chartData}
          x="x"
          y="y"
          style={{
            data: {
              fill: ({ datum }: any) => getBarColor(datum.y),
              width: 25,
            },
          }}
          cornerRadius={4}
          barRatio={0.7}
          {...(!isWeb
            ? {
                animate: {
                  duration: 500,
                  onLoad: { duration: 500 },
                },
              }
            : {})}
        />
      </VictoryChart>
    </View>
  );
};

export default MetricsChart;
